import { useInfiniteQuery } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useAtomValue } from 'jotai';
import { Plus } from 'lucide-react-native';
import { useMemo, type ReactNode } from 'react';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { twJoin, twMerge } from 'tailwind-merge';

import { useAskConversationContext, useAskUiStoreApi } from './LibraryChatContext';
import { formatPublishedAt } from './formatPublishedAt';

import { Button, ButtonIcon, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Pressable } from '@/components/ui/pressable';
import { ScrollView } from '@/components/ui/scroll-view';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import type { AskConversationSummary } from '@/services/ask/api';
import { getAskConversationsV2QueryOptions } from '@/services/ask/queryOptions';
import { userIdAtom } from '@/services/globalState';

type LibraryChatHistoryProps = {
  /** Called after a navigation, so the drawer holding the list can close. */
  onNavigate?: () => void;
  hasLeadingToggle?: boolean;
};

/** Load the next page once the reader is within this many points of the end. */
const LOAD_MORE_THRESHOLD = 200;

function isNearEnd({ layoutMeasurement, contentOffset, contentSize }: NativeScrollEvent): boolean {
  return layoutMeasurement.height + contentOffset.y >= contentSize.height - LOAD_MORE_THRESHOLD;
}

/**
 * The reader's saved conversations, newest first, paged as they scroll.
 *
 * Moving between conversations replaces the route rather than pushing it, so
 * Back always leaves Research instead of stepping through every
 * conversation opened along the way.
 *
 * A plain scroll view rather than a virtualized list: history pages are 20
 * rows, so virtualization buys nothing here. It is the same list in the
 * desktop column and the compact drawer, which has no drag gesture for a
 * nested scroll view to fight.
 *
 * A topic the server has not named reads "New research topic" with its date
 * beneath, so several of them can be told apart. The server never names a
 * topic whose first turn failed, so such a row keeps this form.
 *
 * On desktop the column's collapse toggle floats over the top-left corner, so
 * the heading moves aside for it and centers on the toggle's line.
 */
export function LibraryChatHistory({ onNavigate, hasLeadingToggle }: LibraryChatHistoryProps) {
  const router = useRouter();

  const userId = useAtomValue(userIdAtom);

  const store = useAskUiStoreApi();

  const { conversationId, isNew, snapshot } = useAskConversationContext();

  const { data, isPending, isError, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery(getAskConversationsV2QueryOptions(userId));

  const conversations = useMemo(
    () => data?.pages.flatMap((page) => page.conversations) ?? [],
    [data]
  );

  const isEmptyNew = isNew && (snapshot?.turns.length ?? 0) === 0;

  /*
   * The arrival notice explains how the reader got to the screen they landed
   * on; any move they make themselves makes it stale.
   */
  const openConversation = (conversation: AskConversationSummary) => {
    store.getState().setNotice(null);

    if (conversation.id !== conversationId) {
      store.getState().setNextEntry('history');

      router.replace(ROUTES.LIBRARY_CHAT_CONVERSATION(conversation.id));
    }

    onNavigate?.();
  };

  const startNew = () => {
    store.getState().setNotice(null);

    router.replace(ROUTES.LIBRARY_CHAT);

    onNavigate?.();
  };

  const loadMoreNearEnd = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (hasNextPage && !isFetchingNextPage && isNearEnd(event.nativeEvent)) {
      fetchNextPage().catch(() => {});
    }
  };

  const renderEmpty = (): ReactNode => {
    if (isPending) {
      return <Spinner className="mt-4" accessibilityLabel="Loading your previous conversations" />;
    }

    if (isError) {
      return (
        <VStack className="items-start gap-2 px-1">
          <Text className="text-sm text-typography-700">
            Your previous conversations couldn't be loaded.
          </Text>

          <Button size="sm" variant="link" action="secondary" onPress={() => refetch()}>
            <ButtonText className="text-typography-900 underline">Try again</ButtonText>
          </Button>
        </VStack>
      );
    }

    return (
      <Text className="px-1 text-sm text-typography-700">
        Your conversations are saved here, on every device you sign in to.
      </Text>
    );
  };

  const rows =
    conversations.length === 0
      ? renderEmpty()
      : conversations.map((conversation) => {
          const isOpen = conversation.id === conversationId;

          const title = conversation.title ?? 'New research topic';

          const untitledDate =
            conversation.title == null ? formatPublishedAt(conversation.updatedAt) : null;

          return (
            <Pressable
              key={conversation.id}
              onPress={() => openConversation(conversation)}
              accessibilityRole="button"
              accessibilityLabel={`Open topic: ${title}${untitledDate ? `, ${untitledDate}` : ''}`}
              accessibilityState={{ selected: isOpen }}
              className={twJoin(
                'rounded-lg px-2 py-2 web:cursor-pointer web:hover:bg-background-100',
                isOpen && 'bg-background-100'
              )}>
              <Text
                className={twJoin('text-sm text-typography-900', isOpen && 'font-semibold')}
                numberOfLines={2}>
                {title}
              </Text>

              {untitledDate ? (
                <Text className="text-xs text-typography-700">{untitledDate}</Text>
              ) : null}
            </Pressable>
          );
        });

  const footer = isFetchingNextPage ? (
    <Spinner accessibilityLabel="Loading more conversations" />
  ) : null;

  const scrollProps = {
    className: 'min-h-0 flex-1',
    contentContainerClassName: 'gap-1',
    onScroll: loadMoreNearEnd,
    scrollEventThrottle: 100,
  };

  return (
    <VStack className="min-h-0 flex-1 gap-3 p-4">
      <Text
        className={twMerge(
          'px-1 font-semibold text-xs uppercase text-typography-700',
          hasLeadingToggle && '-mt-2 pl-10 leading-10'
        )}>
        Previous conversations
      </Text>

      <ScrollView {...scrollProps}>
        {rows}

        {footer}
      </ScrollView>

      <HStack className="justify-end">
        <Button onPress={startNew} isDisabled={isEmptyNew}>
          <ButtonIcon as={Plus} />
          <ButtonText>New conversation</ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
}
